<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <admin-sub-menu />
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Import Filers
            </h2>
            <div class="subtitle is-2">
              Use the pre-formatted csv file.
            </div>
          </div>
        </div>
        <div
          v-if="isImporting || isUploading"
          class="columns is-centered"
        >
          <div class="column is-8">
            <div class="message is-warning">
              <div class="message-body">
                There's currently a file being imported. You won't be able to upload a new file until the current import is completed.
              </div>
            </div>
          </div>
        </div>
        <div class="columns is-centered">
          <div class="column is-8">
            <div class="field">
              <div class="file has-name is-fullwidth">
                <label class="file-label">
                  <input
                    class="file-input"
                    type="file"
                    name="resume"
                    accept=".csv"
                    :disabled="isImporting || isUploading"
                    @change="updateFileState"
                  >
                  <span class="file-cta">
                    <span class="file-icon">
                      <i class="fas fa-upload" />
                    </span>
                    <span class="file-label">
                      Select Users CSV…
                    </span>
                  </span>
                  <span class="file-name">
                    {{ fileName ? fileName : 'No file selected' }}
                  </span>
                </label>
              </div>
            </div>
            <div class="field has-text-centered">
              <button
                id="import-btn"
                type="button"
                class="button is-primary"
                :class="isImporting || isUploading ? 'is-loading' : ''"
                :disabled="isImporting || isUploading"
                @click="parseFile"
              >
                Start Import
              </button>
              <template v-if="isImporting || isUploading">
                &nbsp; <button
                  id="cancel-import-btn"
                  type="button"
                  class="button is-danger"
                  :class="isCanceling ? 'is-loading' : ''"
                  :disabled="isCanceling"
                  @click="cancelImportProcess"
                >
                  Cancel Import
                </button>
              </template>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column has-text-centered">
            <div class="section">
              <h3 class="title is-size-4">
                {{ isImporting || isUploading ? 'Import Progress' : 'Last Import' }}
              </h3>
              <div class="subtitle is-size-6">
                {{ isImporting ? `Started on: ${formatDate(importStatus.startedOn, 'MM/DD/YYYY hh:mm A')}` : '' }}
                {{ importStatus.endedOn ? `Ended on: ${formatDate(importStatus.endedOn, 'MM/DD/YYYY hh:mm A')}` : '' }}
              </div>
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>
                      Status: {{ isUploading ? 'Uploading file...' : importStatus.status }}
                    </th>
                    <th>
                      Batch #: {{ isUploading ? '---' : importStatus.id }}
                    </th>
                    <th>
                      Percentage Completed: {{ isUploading ? 0 : importStatus.percentComplete }}%
                    </th>
                  </tr>
                </thead>
              </table>
              <div class="progress-bar-bar">
                <progress
                  class="progress is-info"
                  :value="isUploading ? 0 : importStatus.percentComplete"
                  max="100"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  :aria-valuenow="importStatus.percentComplete"
                  role="progressbar"
                >
                  {{ importStatus.percentComplete }}%
                </progress>
              </div>
            </div>
            <div class="section">
              <h4 class="title is-size-4">
                {{ isImporting ? 'Current Import Stats' : 'Last Import Stats' }}
              </h4>
              <div class="level">
                <div class="level-item has-text-centered">
                  <div>
                    <p class="title">
                      {{ isUploading ? 0 : importStatus.totalUsers }}
                    </p>
                    <p class="heading">
                      Total # of Users
                    </p>
                  </div>
                </div>
                <div class="level-item has-text-centered">
                  <div>
                    <p class="title">
                      {{ isUploading ? 0 : importStatus.processedUsers }}
                    </p>
                    <p class="heading">
                      Processed Users
                    </p>
                  </div>
                </div>
                <div class="level-item has-text-centered">
                  <div>
                    <p class="title">
                      {{ isUploading ? 0 : importStatus.updatedUsers }}
                    </p>
                    <p class="heading">
                      Updated Users
                    </p>
                  </div>
                </div>
                <div class="level-item has-text-centered">
                  <div>
                    <p class="title">
                      {{ isUploading ? 0 : importStatus.failedUsers }}
                    </p>
                    <p class="heading">
                      Failed to Import Users
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="importStatus.usersImportResult && importStatus.usersImportResult.length"
              class="section"
            >
              <div class="columns is-multiline">
                <div class="column is-12 has-text-centered">
                  <h4 class="title is-size-4">
                    <span class="icon has-text-danger is-small">
                      <i class="fal fa-exclamation-triangle" />
                    </span> &nbsp; Users Not Imported
                  </h4>
                </div>
                <div class="column is-12 has-text-left">
                  <div
                    v-for="user in importStatus.usersImportResult"
                    :key="user.id"
                    class="message"
                  >
                    <div class="message-body">
                      <b>id: </b>{{ user.userName }}<br>
                      <b>username: </b>{{ user.userName }}<br>
                      <b>e-mail: </b>{{ user.emailAddress }}<br>
                      <b>status: </b>{{ user.status }}<br>
                      <b>message: </b>{{ user.message }}<br>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import Papa from 'papaparse';
import AdminSubMenu from "@/components/AdminSubMenu";

export default {
  components: {
    AdminSubMenu,
  },
  data() {
    return {
      file: null,
      fileName: null,
      timeOut: null,
      gotError: false,
      startOverState: false,
      isSaving: false,
      isUploading: false,
      isCanceling: false,
    };
  },
  computed: {
    isImporting () {
      if (this.importStatus && this.importStatus.status === 'Processing' || this.importStatus.status === 'Saved') {
        return true;
      }
      return false;
    },
    importStatus () {
      return this.$store.state.user.importStatus;
    },
  },
  watch : {
    "importStatus.status": {
      handler (newValue) {
        console.log(newValue);
        if (newValue === 'Saved') {
          this.isUploading = false;
        }
      },
      deep: true,
    },
  },
  created () {
    this.$store.commit('loading', false);
  },
  mounted() {
    this.checkStatus();
  },
  methods: {
    async cancelImportProcess () {
      this.isCanceling = true;
      if (this.importStatus.id) {
        await this.$store.dispatch('user/stopImportBatchProcesss', this.importStatus.id);
      } else {
        setTimeout(this.cancelImportProcess, 500);
      }
    },
    clearFile () {
      this.file = null;
      this.fileName = null;
    },
    async checkStatus() {
      await this.$store.dispatch('user/checkBatchStatus');
      if (this.isImporting) {
        this.clearFile();
        this.isUploading = false;
        setTimeout(this.checkStatus, 2000);
      }
      if (this.isUploading) {
        setTimeout(this.checkStatus, 2000);
      }
      if (this.importStatus.state === 'Cancelled') {
        this.clearFile();
        this.isCanceling = false;
      }
    },
    updateFileState (event) {
      if (event.target.files.length) {
        Vue.set(this, 'file', event.target.files[0]);
        this.fileName = event.target.files[0].name;
      }
    },
    parseFile() {
      if(!this.gotError) {
        if (this.file) {
          Papa.parse(this.file, {
            skipEmptyLines: "greedy",
            complete: async (results) => {
              if (results.errors.length > 0) {
                alert("Display CSV errors!");
              } else {

                const indexes = results.data.shift();
                let importing = [];

                results.data.forEach(row => {
                  importing.push(
                    {
                      "userName": row[indexes.indexOf("EMPLOYEE_NUMBER")],
                      "emailAddress": row[indexes.indexOf("EMAIL_ADDRESS")],
                      "firstName": row[indexes.indexOf("FIRST_NAME")],
                      "lastName": row[indexes.indexOf("LAST_NAME")],
                      "mayorForm": row[indexes.indexOf("MAYOR_FORM")],
                      "cityForm": row[indexes.indexOf("CITY_FORM")],
                      "stateForm": row[indexes.indexOf("STATE_FORM")],
                      "homeStreetAddress": row[indexes.indexOf("ADDRESS")],
                      "homeState": row[indexes.indexOf("STATE")],
                      "homeCity": row[indexes.indexOf("CITY")],
                      "homeZip": row[indexes.indexOf("ZIP")],
                      "positions": [
                        {
                          "publicPosition": row[indexes.indexOf("POSITION")],
                          "affiliationStatus": 35,
                          "profession": row[indexes.indexOf("POSITION")],
                          "agencyId": parseInt(row[indexes.indexOf("DEPARTMENT_NUMBER")]),
                        },
                      ],
                    },
                  );
                });

                this.checkStatus();
                this.isUploading = true;
                this.$store.dispatch('user/importUsers', importing);

              }
            },
            error: err => {
              this.$error(err);
            },
          });
        }
      }
    },
  },
};
</script>
<style lang="scss">
  .progress-bar-bar {
    border: 1px solid $sidewalk-grey;
    padding: 0.2rem;
  }
  table {
    th {
      text-align: center !important;
      width: 33%;
    }
  }
</style>
